import type {PayloadAction} from "@reduxjs/toolkit";
import {createSlice} from "@reduxjs/toolkit";

import type {ApiTokenCredentials} from "../api/credentials";
import type {RevaPermission} from "../util/revaPermission";

export interface AuthData {
    username: string;
    name: string | undefined;
    license: string;
    licenseId: number;
    licenseDisplayName?: string | null;
    /** #19351: stored e-mail address, if any. */
    email?: string | null;
    emailVerified?: boolean;
    /** #19351: show the hint on a missing/unverified address (staged rollout, decided by reva-rest). */
    emailVerificationHint?: boolean;
    /** #19302 S-B9: Links ins EVA+ zeigen; `undefined` = älteres reva-rest ohne das Feld. */
    showEvaPlusLinks?: boolean;
}

interface AuthState {
    loggedIn: boolean;
    authData: AuthData | null;
    token: string | null;
    permissions: RevaPermission[];
}

export interface LoginPayload {
    authData: AuthData;
    token: string;
    permissions: RevaPermission[];
}

/** #19351: payload of {@link updateProfile} — the fields PUT /user/profile can change. */
export interface UpdateProfilePayload {
    name: string | undefined;
    email: string | null;
    emailVerified: boolean;
}

const initialState: AuthState = {
    loggedIn: false,
    authData: null,
    token: null,
    permissions: [],
};

export const authSlice = createSlice({
    name: "auth",
    initialState,
    reducers: {
        logout: (state) => {
            state.loggedIn = false;
            state.authData = null;
            state.token = null;
            state.permissions = [];
        },
        login: (state, action: PayloadAction<LoginPayload>) => {
            const {authData, token, permissions} = action.payload;
            state.loggedIn = true;
            state.authData = authData;
            state.token = token;
            state.permissions = permissions;
        },
        /** #19351: applies a successful PUT /user/profile to the auth store (name/e-mail/verification). */
        updateProfile: (state, action: PayloadAction<UpdateProfilePayload>) => {
            if (!state.authData) return;
            state.authData.name = action.payload.name;
            state.authData.email = action.payload.email;
            state.authData.emailVerified = action.payload.emailVerified;
        },
    },
    selectors: {
        selectLoggedIn: (state) => state.loggedIn,
        selectUsername: (state) => state.authData?.username ?? "",
        selectFullName: (state) => state.authData?.name ?? null,
        selectLicenseName: (state) => state.authData?.license ?? null,
        selectLicenseDisplayName: (state) => state.authData?.licenseDisplayName ?? null,
        selectEmail: (state) => state.authData?.email ?? null,
        selectEmailVerified: (state) => state.authData?.emailVerified ?? false,
        selectEmailVerificationHint: (state) => state.authData?.emailVerificationHint ?? false,
        selectShowEvaPlusLinks: (state) => state.authData?.showEvaPlusLinks,
        selectPermissions: (state) => state.permissions,
        selectCredentials: (state): ApiTokenCredentials | null => {
            if (!state.token) return null;
            return {token: state.token};
        },
    },
});

export const {logout, login, updateProfile} = authSlice.actions;

export const {
    selectLoggedIn,
    selectUsername,
    selectFullName,
    selectLicenseName,
    selectLicenseDisplayName,
    selectEmail,
    selectEmailVerified,
    selectEmailVerificationHint,
    selectShowEvaPlusLinks,
    selectPermissions,
    selectCredentials,
} = authSlice.selectors;

export default authSlice.reducer;
